import { PlayIcon } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

import { TestRunInputVariables } from "./TestRunInputVariables";
import { TestRunStatusDisplay } from "./TestRunStatusDisplay";
import {
  TestRunMessage,
  TestRunStatus,
  useWorkflowTestRunWebSocket,
} from "./useWorkflowTestRunWebSocket";

import { ENDPOINTS } from "@/api/application-api";
import { Variable } from "@/components/InputVariableConfigurator/types";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import {
  DashboardProviderApiKeyErrorCode,
  isDashboardApiKeyHttpError,
} from "@/types/dashboard-api-key-api-error";
import { authHeader } from "@/utils/headers";
import { isModifierKeyPressed, SHORTCUTS } from "@/utils/shortcuts";
import { displayErrorToast } from "@/utils/toast";

// Constants
const TEST_RUN_TIMEOUT_SECONDS = 300; // 5 minutes

interface TestRunModalContentProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  workflowId: string;
  workflowName: string;
  versionNumber: string;
  workspaceId: string;
  inputVariables: Variable[];
}

type InputValues = Record<string, string | object>;

interface TestRunState {
  status: TestRunStatus | "idle";
  runId: string | null;
  traceId: string | null;
  message: string | React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any;
  error: {
    code: string;
    message: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details?: Record<string, any>;
  } | null;
  progress?: number;
  summary?: {
    total_nodes?: number;
    slowest_node?: { id: string; duration_ms: number };
    token_usage?: {
      prompt: number;
      completion: number;
      total: number;
      cost_usd?: number;
    };
  };
  duration?: number;
  providerApiKeyPrompt?: {
    provider: string;
  } | null;
}

export const TestRunModalContent: React.FC<TestRunModalContentProps> = ({
  isOpen,
  setIsOpen,
  workflowId,
  workflowName,
  versionNumber,
  workspaceId,
  inputVariables,
}) => {
  const [inputValues, setInputValues] = useState<InputValues>({});
  const [testRunState, setTestRunState] = useState<TestRunState>({
    status: "idle",
    runId: null,
    traceId: null,
    message: "",
    result: null,
    error: null,
    providerApiKeyPrompt: null,
  });

  const user = useUser();
  const userToken = useUserToken();

  // Initialize input values when modal opens or variables change
  useEffect(() => {
    if (isOpen) {
      const initialValues: InputValues = {};
      inputVariables.forEach(variable => {
        initialValues[variable.key] = variable.value || "";
      });
      setInputValues(initialValues);

      // Reset test run state when modal opens
      setTestRunState({
        status: "idle",
        runId: null,
        traceId: null,
        message: "",
        result: null,
        error: null,
        providerApiKeyPrompt: null,
      });
    }
  }, [isOpen, inputVariables]);

  // Handle WebSocket messages
  const handleWebSocketMessage = (message: TestRunMessage) => {
    // eslint-disable-next-line default-case
    switch (message.type) {
      case "kicked_off":
        setTestRunState(prev => ({
          ...prev,
          status: TestRunStatus.QUEUED,
          message: "Workflow execution queued...",
        }));
        break;

      case "update":
        if (message.data.status === "RUNNING" && message.data.trace_id) {
          setTestRunState(prev => ({
            ...prev,
            status: TestRunStatus.RUNNING,
            traceId: message.data.trace_id || null,
            message: message.data.info || "Workflow is running...",
            progress: message.data.progress?.percentage,
          }));
        } else {
          setTestRunState(prev => ({
            ...prev,
            message: message.data.info || prev.message,
            progress: message.data.progress?.percentage || prev.progress,
          }));
        }
        break;

      case "succeeded":
        setTestRunState(prev => ({
          ...prev,
          status: TestRunStatus.SUCCESS,
          result: message.data.final_output,
          summary: message.data.summary,
          duration: message.data.duration_ms,
          traceId: message.data.trace_id || prev.traceId,
          message: "Workflow execution completed successfully!",
        }));
        break;

      case "failed":
        setTestRunState(prev => ({
          ...prev,
          status: TestRunStatus.FAILED,
          error: message.data.error,
          duration: message.data.duration_ms,
          traceId: message.data.trace_id || prev.traceId,
          message: "Workflow execution failed",
          providerApiKeyPrompt: null,
        }));
        break;
    }
  };

  // Subscribe to WebSocket messages for the current run
  useWorkflowTestRunWebSocket({
    runId: testRunState.runId,
    onMessage: handleWebSocketMessage,
  });

  const handleInputChange = (key: string, value: string) => {
    setInputValues(prev => ({ ...prev, [key]: value }));
  };

  const parseInputValue = (value: string): string | object => {
    if (!value.trim()) {
      return "";
    }

    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const handleRunWorkflow = useCallback(async () => {
    if (!workflowId || !workspaceId || !user?.activeWorkspaceId) {
      setTestRunState(prev => ({
        ...prev,
        status: TestRunStatus.FAILED,
        error: {
          code: "INVALID_CONFIG",
          message: "Missing workflow information",
        },
      }));
      return;
    }

    // Reset state for new run
    setTestRunState({
      status: TestRunStatus.QUEUED,
      runId: null,
      traceId: null,
      message: "Starting workflow execution...",
      result: null,
      error: null,
      providerApiKeyPrompt: null,
    });

    // Process input values
    const processedInputs: Record<string, string | object> = {};
    Object.entries(inputValues).forEach(([key, value]) => {
      processedInputs[key] = parseInputValue(value as string);
    });

    try {
      // Use the new test_run_workflow endpoint with workflow name
      const testRunUrl = ENDPOINTS.test_run_workflow(user.activeWorkspaceId, workflowName);

      const requestBody = {
        input_variables: processedInputs,
        version_number: Number(versionNumber),
        test_config: {
          timeout_seconds: TEST_RUN_TIMEOUT_SECONDS,
        },
        metadata: {
          trigger_source: "modal",
        },
      };

      const response = await fetch(testRunUrl, {
        method: "POST",
        headers: {
          ...authHeader(userToken),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (
          isDashboardApiKeyHttpError(errorData) &&
          errorData.error_code === DashboardProviderApiKeyErrorCode.PROVIDER_KEY_REQUIRED &&
          errorData.provider
        ) {
          const { provider } = errorData;
          setTestRunState(prev => ({
            ...prev,
            status: TestRunStatus.FAILED,
            message: errorData.message,
            error: {
              code: errorData.error_code,
              message: errorData.message,
            },
            providerApiKeyPrompt: {
              provider,
            },
          }));
          return;
        }

        displayErrorToast(errorData?.message || `Failed to run workflow: ${errorData?.message}`);

        throw new Error(`Failed to run workflow: ${errorData?.message}`);
      }

      const data = await response.json();

      if (data.success) {
        // Store the run_id for WebSocket subscription
        setTestRunState(prev => ({
          ...prev,
          runId: data.run_id,
          message: data.message || "Workflow execution started",
        }));

        // WebSocket will handle all status updates from here
      } else {
        setTestRunState(prev => ({
          ...prev,
          status: TestRunStatus.FAILED,
          error: {
            code: "START_FAILED",
            message: data.error?.message || "Failed to start workflow",
          },
          providerApiKeyPrompt: null,
        }));
      }
    } catch (error) {
      setTestRunState(prev => ({
        ...prev,
        status: TestRunStatus.FAILED,
        error: {
          code: "REQUEST_FAILED",
          message: error instanceof Error ? error.message : "Failed to start workflow execution",
        },
        providerApiKeyPrompt: null,
      }));
    }
  }, [workflowId, workflowName, workspaceId, user, userToken, versionNumber, inputValues]);

  // Add keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isModifierKeyPressed(event) && event.key === "Enter") {
        if (!isOpen) {
          return;
        }
        if (
          testRunState.status === TestRunStatus.QUEUED ||
          testRunState.status === TestRunStatus.RUNNING
        ) {
          return;
        }
        event.preventDefault();
        handleRunWorkflow();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, testRunState.status, handleRunWorkflow]);

  const isRunning =
    testRunState.status === TestRunStatus.QUEUED || testRunState.status === TestRunStatus.RUNNING;

  return (
    <Dialog key="test-run-modal" open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="flex max-h-[85vh] max-w-xl flex-col gap-0 overflow-hidden p-0">
        <div className="border-b border-gray-100 px-6 pb-4 pt-6">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                <PlayIcon className="h-4 w-4 text-blue-600" />
              </div>
              Workflow Test Run
              <div className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                Version {versionNumber}
              </div>
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-gray-600">
              {inputVariables.length > 0
                ? "Configure the input variables below and test your workflow."
                : "This workflow has no input variables. Click 'Run Workflow' to start execution."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto pb-4">
          {inputVariables.length > 0 && (
            <div className="max-h-[50vh] space-y-4 overflow-y-auto px-6 py-4">
              <div className="mb-4 text-sm font-semibold text-gray-900">Input Variables</div>
              <TestRunInputVariables
                inputVariables={inputVariables}
                inputValues={inputValues}
                onInputChange={handleInputChange}
              />
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 px-6 py-4">
          <TestRunStatusDisplay
            status={testRunState.status}
            message={testRunState.message as string}
            traceId={testRunState.traceId}
            result={testRunState.result}
            error={testRunState.error}
            progress={testRunState.progress}
            summary={testRunState.summary}
            duration={testRunState.duration}
            workspaceId={workspaceId}
            providerApiKeyPrompt={
              testRunState.providerApiKeyPrompt
                ? {
                    provider: testRunState.providerApiKeyPrompt.provider,
                    onAfterProviderKeySaved: () => {
                      handleRunWorkflow();
                    },
                  }
                : null
            }
          />

          <DialogFooter className="m-0 flex gap-3">
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isRunning}>
              {testRunState.status === TestRunStatus.SUCCESS ? "Close" : "Cancel"}
            </Button>
            {testRunState.status === TestRunStatus.FAILED && !testRunState.providerApiKeyPrompt && (
              <Button onClick={handleRunWorkflow} disabled={isRunning} className="min-w-[120px]">
                <PlayIcon className="mr-2 h-4 w-4" />
                <span>Try Again</span>
                <span className="ml-2 select-none opacity-60">{SHORTCUTS.SUBMIT.display}</span>
              </Button>
            )}
            {(testRunState.status === "idle" || testRunState.status === TestRunStatus.SUCCESS) && (
              <Button onClick={handleRunWorkflow} disabled={isRunning} className="min-w-[120px]">
                <PlayIcon className="mr-2 h-4 w-4" />
                <span>Run</span>
                <span className="ml-2 select-none opacity-60">{SHORTCUTS.SUBMIT.display}</span>
              </Button>
            )}
            {isRunning && (
              <Button disabled className="min-w-[120px]">
                <LoadingSpinner size={4} className="mr-2" />
                <span>Running...</span>
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
